import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useWaitForCeleryTaskResultsAsync } from '@/hooks/useWaitForCeleryTaskResultsAsync'
import { usePingCamera } from '@/queries/cameraQueries'
import { showToast } from '@/theme/notifications'

type UseCameraPingParams = {
  deviceId?: string
  cameraId?: string
  onSuccess?: () => void
}

export const useCameraPing = ({
  deviceId,
  cameraId,
  onSuccess
}: UseCameraPingParams) => {
  const intl = useIntl()

  const { mutateAsync: pingCamera, isPending: isPingingCamera } =
    usePingCamera()

  const { waitForCeleryTaskResultsAsync, isPending: isPolling } =
    useWaitForCeleryTaskResultsAsync()

  const handlePing = useCallback(async () => {
    if (!deviceId || !cameraId) {
      return
    }

    try {
      const { task_id } = await pingCamera({ deviceId, cameraId })
      await waitForCeleryTaskResultsAsync(task_id)

      onSuccess?.()
      showToast(intl.formatMessage({ id: 'cameras.ping.success' }), 'success')
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'cameras.ping.error' })
        ),
        'error'
      )
    }
  }, [
    pingCamera,
    waitForCeleryTaskResultsAsync,
    deviceId,
    cameraId,
    onSuccess,
    intl
  ])

  return useMemo(
    () => ({
      isPinging: isPingingCamera || isPolling,
      pingCamera: handlePing
    }),
    [isPingingCamera, isPolling, handlePing]
  )
}
